'use client'

import { useState, useEffect } from 'react'
import { useSearchParams, useRouter } from 'next/navigation'

import JobsResults from './JobsResults'
import useLocale from '@hooks/client/useLocale'
import styles from '../jobs.module.scss'

export default function JobsContent(): JSX.Element {
  const searchParams = useSearchParams()
  const router = useRouter()
  const locale = useLocale()
  
  const jobTitle = searchParams.get('query')
  const locationName = searchParams.get('location_name')
  const locationId = searchParams.get('location_id')
  
  // Show content if location is provided (query can be empty when navigating through category/state/county)
  const hasSearch = locationName || locationId

  // Map display values to API sort values
  const sortMap: Record<string, string> = {
    'FEATURED': 'Most Relevant',
    'MOST_RECENT': 'Newest',
    'NEAREST': 'Nearest',
  }

  const reverseSortMap: Record<string, string> = {
    'Most Relevant': 'FEATURED',
    'Newest': 'MOST_RECENT',
    'Nearest': 'NEAREST',
  }

  // Initialize sort from URL params
  const [sortValue, setSortValue] = useState<string>(() => {
    const sort = searchParams.get('sort') || 'FEATURED'
    return sortMap[sort] || 'Most Relevant'
  })

  // Update sort when URL params change
  const currentSort = searchParams.get('sort') || 'FEATURED'
  const displayValue = sortMap[currentSort] || 'Most Relevant'
  
  useEffect(() => {
    setSortValue(displayValue)
  }, [displayValue])

  const handleSortChange = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    const selectedSort = e.target.value
    const apiSortValue = reverseSortMap[selectedSort] || 'FEATURED'
    
    // Build new URL params, preserving existing params
    const params = new URLSearchParams()
    
    // Preserve all existing search params
    searchParams.forEach((value, key) => {
      params.set(key, value)
    })
    
    // Update sort and reset to page 1
    params.set('sort', apiSortValue)
    params.set('page', '1')
    
    router.replace(`/${locale}/v1/jobs?${params.toString()}`, { scroll: false })
  }

  if (!hasSearch) {
    return <></>
  }

  const titleize = (value: string): string =>
    value
      .split(' ')
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')

  // Format location - check if state is already in the location name
  const getFormattedLocation = (): string => {
    if (!locationName) return ''
    
    const titleizedLocation = titleize(locationName)
    const locationState = searchParams.get('location_state')
    
    // Check if location name already contains the state (e.g., "San Jose, CA")
    if (locationState) {
      const locationUpper = locationName.toUpperCase()
      const stateUpper = locationState.toUpperCase()
      
      // If location already ends with the state, don't add it again
      if (locationUpper.endsWith(`, ${stateUpper}`) || locationUpper.endsWith(` ${stateUpper}`)) {
        return titleizedLocation
      }
      
      // Otherwise, add the state (uppercase)
      return `${titleizedLocation}, ${locationState.toUpperCase()}`
    }
    
    return titleizedLocation
  }

  const formattedJobTitle = titleize(jobTitle || '')
  const formattedLocation = getFormattedLocation()
  
  // Format header text based on whether we have a job title
  const headerText = jobTitle 
    ? `${formattedJobTitle} Jobs in ${formattedLocation}`
    : `Jobs in ${formattedLocation}`

  return (
    <>
      <div className={styles.jobsContentHeader}>
        <div>
          <h1 className={styles.jobsContentTitle}>
            {headerText}
          </h1>
        </div>
        <div className={styles.jobsSortWrapper}>
          <span className={styles.jobsSortLabel}>Sort by:</span>
          <div className={styles.jobsSelectWrapper}>
            <select 
              className={styles.jobsSortSelect}
              value={sortValue}
              onChange={handleSortChange}
            >
              <option value="Most Relevant">Most Relevant</option>
              <option value="Newest">Newest</option>
              <option value="Nearest">Nearest</option>
            </select>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={styles.jobsSelectIcon}>
              <path d="m6 9 6 6 6-6"></path>
            </svg>
          </div>
        </div>
      </div>
      <button className={styles.jobsRefineButton}>
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="m6 9 6 6 6-6"></path>
        </svg>
        Refine
      </button>
      <JobsResults />
    </>
  )
}
